<template>
    <div>
        <v-alert color="info" text>
            {{ $t('Logon tulee olla PNG-tiedosto, jonka tausta on läpinäkyvä.') }}
        </v-alert>

        <div class="logo-preview-wrapper" style="max-width: 600px;">
            <div class="checkered-bg">
                <img v-if="previewSrc || currentLogo" :src="previewSrc || currentLogo" class="logo-preview" />
                <div v-else class="empty-logo text--secondary">
                    {{ $t('Ei logoa') }}
                </div>
            </div>
        </div>

        <div class="mt-4">
            <input
                ref="fileInput"
                type="file"
                accept="image/png"
                class="d-none"
                @change="onFileChange"
            />
            <v-btn outlined class="mr-2" @click="pickFile" :disabled="isSubmitting">
                {{ $t("Valitse logo") }}
            </v-btn>
            <v-btn
                color="primary"
                @click="saveLogo"
                :loading="isSubmitting"
                :disabled="!selectedFile"
            >
                {{ $t("Tallenna") }}
            </v-btn>
        </div>

        <v-alert v-if="errorMessage" type="error" text class="mt-4" dismissible @input="errorMessage = ''">
            {{ errorMessage }}
        </v-alert>
    </div>
</template>

<script>
import { ref, computed, onBeforeUnmount } from "@vue/composition-api";
import adminApi from "@/api/admin";
import api from "@/api";
import useAuth from "@/useAuth";
import { i18n } from "@/plugins/i18n";

const MAX_FILE_BYTES = 5 * 1024 * 1024;

const hasTransparentPixel = (file) =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
                let transparent = false;
                for (let i = 3; i < data.length; i += 4) {
                    if (data[i] < 255) {
                        transparent = true;
                        break;
                    }
                }
                resolve(transparent);
            } catch (e) {
                reject(e);
            } finally {
                URL.revokeObjectURL(url);
            }
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("image_load_failed"));
        };
        img.src = url;
    });

export default {
    setup() {
        const { tenant, setTenant } = useAuth();

        const fileInput = ref(null);
        const selectedFile = ref(null);
        const previewSrc = ref("");
        const isSubmitting = ref(false);
        const errorMessage = ref("");

        const currentLogo = computed(() => {
            const logo = tenant.value && tenant.value.logo;
            if (!logo) return "";
            if (typeof logo === "string") return logo;
            return logo.full_size || "";
        });

        const pickFile = () => {
            errorMessage.value = "";
            fileInput.value && fileInput.value.click();
        };

        const onFileChange = async (event) => {
            const file = event.target.files && event.target.files[0];
            event.target.value = "";
            if (!file) return;

            errorMessage.value = "";

            if (!/\.png$/i.test(file.name)) {
                errorMessage.value = i18n.t("Logon tulee olla PNG-tiedosto.");
                return;
            }
            if (file.size > MAX_FILE_BYTES) {
                errorMessage.value = i18n.t("Tiedosto on liian suuri (max 5 MB).");
                return;
            }

            try {
                const transparent = await hasTransparentPixel(file);
                if (!transparent) {
                    errorMessage.value = i18n.t("Logon tausta tulee olla läpinäkyvä.");
                    return;
                }
            } catch (e) {
                errorMessage.value = i18n.t("Tiedostoa ei voitu lukea kuvana.");
                return;
            }

            if (previewSrc.value) URL.revokeObjectURL(previewSrc.value);
            previewSrc.value = URL.createObjectURL(file);
            selectedFile.value = file;
        };

        const saveLogo = async () => {
            if (!selectedFile.value) return;
            isSubmitting.value = true;
            errorMessage.value = "";
            try {
                const res = await adminApi.updateTenantLogo(selectedFile.value);
                if (res.ok) {
                    const tenantRes = await api.getTenant();
                    if (tenantRes.ok) {
                        setTenant(tenantRes.data);
                    }
                    if (previewSrc.value) {
                        URL.revokeObjectURL(previewSrc.value);
                        previewSrc.value = "";
                    }
                    selectedFile.value = null;
                } else {
                    const detail =
                        (res.data && (res.data.detail || res.data.logo && res.data.logo[0])) ||
                        i18n.t("Logon tallennus epäonnistui.");
                    errorMessage.value = detail;
                }
            } catch (e) {
                errorMessage.value = i18n.t("Logon tallennus epäonnistui.");
            } finally {
                isSubmitting.value = false;
            }
        };

        onBeforeUnmount(() => {
            if (previewSrc.value) URL.revokeObjectURL(previewSrc.value);
        });

        return {
            fileInput,
            selectedFile,
            previewSrc,
            isSubmitting,
            errorMessage,
            currentLogo,
            pickFile,
            onFileChange,
            saveLogo,
        };
    },
};
</script>

<style scoped>
.checkered-bg {
    background-color: #fff;
    background-image:
        linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
        linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
        linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
    background-size: 16px 16px;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
}

.logo-preview {
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
}

.empty-logo {
    font-style: italic;
}
</style>
